import type { CSSProperties, ReactElement } from 'react';
import { genreName, useGamesSite } from '../context';
import { genreGradient } from '../genres';
import { gamePagePath } from '../routes';
import type { FeaturedGame } from '../types';
import { GameIcon } from './GameIcon';
import { PlayButton } from './PlayButton';

/**
 * The featured games, one big card each, side by side and swiped through
 * (CSS scroll snapping — it works before any script runs).
 *
 * A card with hero art (an admin's wide picture) shows it edge to edge.
 * Otherwise it wears its own game's colours: the backdrop is the game's icon,
 * blown up and blurred, with the icon itself set on top; a game with no art
 * wears its genre's gradient.
 */
export function HeroCarousel({ featured }: { featured: FeaturedGame[] }): ReactElement | null {
  const { Link, genreLabels } = useGamesSite();
  if (featured.length === 0) return null;
  return (
    <section className="fg-hero-shelf" aria-labelledby="fg-featured">
      <h2 id="fg-featured" className="fg-visually-hidden">
        Featured games
      </h2>
      <ul className="fg-hero-list">
        {featured.map(({ game, label, tagline, heroUrl, heroUrlSmall }, index) => {
          const art = game.iconUrlLarge ?? game.iconUrl;
          const glow: CSSProperties = art
            ? { backgroundImage: `url("${encodeURI(art)}")` }
            : { backgroundImage: genreGradient(game.genres[0] ?? '') };
          const kicker =
            label ?? (game.genres[0] ? genreName(genreLabels, game.genres[0]) : 'Featured');
          return (
            <li key={game.slug} className="fg-hero">
              {heroUrl && heroUrlSmall ? (
                <img
                  className="fg-hero__picture"
                  src={heroUrlSmall}
                  srcSet={`${heroUrlSmall} 800w, ${heroUrl} 1600w`}
                  sizes="(min-width: 860px) 50vw, 100vw"
                  alt=""
                  // The first card is in view; the rest wait for a swipe.
                  loading={index === 0 ? undefined : 'lazy'}
                />
              ) : (
                <>
                  <span className="fg-hero__glow" style={glow} aria-hidden="true" />
                  <div className="fg-hero__art" aria-hidden="true">
                    <GameIcon game={game} size="xl" />
                  </div>
                </>
              )}
              <span className="fg-hero__shade" aria-hidden="true" />
              <div className="fg-hero__copy">
                <p className="fg-kicker fg-kicker--on-art">{kicker}</p>
                <h3 className="fg-hero__title">
                  {/* The title's link covers the card; Play sits above it. */}
                  <Link href={gamePagePath(game.slug)} className="fg-stretch">
                    {game.title}
                  </Link>
                </h3>
                <p className="fg-hero__tagline">{tagline ?? game.description}</p>
              </div>
              <div className="fg-hero__foot">
                <GameIcon game={game} size="sm" />
                <span className="fg-hero__name">
                  <span className="fg-hero__name-title">{game.title}</span>
                  {game.kind === 'listing' && (
                    <span className="fg-hero__name-sub">in {game.archeName}</span>
                  )}
                </span>
                <PlayButton game={game} />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
